/**
 * WHICH LESSON DOES A REPEATING TIME SPEAK FOR? — the one place that answers it.
 *
 * EIFO report #198 (issue #766), part 1 of the approved plan. ⚠️ PART 4 IS NOW
 * LIVE: `repeating-time-label.ts` reads this rule, so every repeating line on
 * every viewer screen speaks for the lesson these two functions pick. Parts 1–3
 * were inert; this module is now the thing that decides what a family is told.
 *
 * ── The bug this exists to make fixable
 *
 * Most screens do not show one lesson on one day. They show a repeating line —
 * "Fridays 7 PM" — for a whole term. That single line has two different right
 * answers inside one term, and today the code always picks the summer one:
 *
 *   `class_sections.{day_of_week,start_time,end_time}` is a weekly triple
 *   composed through `localTimeToUtc` at the summer `REF_DATE` (2024-07-15,
 *   `timezone-utils.ts`), and `projectLocalTimeRange` then re-samples only the
 *   VIEWER's offset. So a repeating label reads the anchor zone's SUMMER
 *   wall-clock in the viewer's SUMMER offset, all year.
 *
 * A Friday 19:00 class, verified arithmetic:
 *
 *   | anchored to      | 23 Oct 2026     | 6 Nov 2026      | who drifts        |
 *   | ---------------- | --------------- | --------------- | ----------------- |
 *   | America/New_York | Beijing Sat 07  | Beijing Sat 08  | the overseas side |
 *   | Asia/Shanghai    | New York Fri 07 | New York Fri 06 | the US side       |
 *
 * Both columns are correct — the lesson's real instant moves when the anchor
 * zone changes its clocks, and a zone that did not move with it reads a
 * different hour afterwards. The defect is only that the repeating LINE cannot
 * say so. Note that the drift is not symmetric about who is hurt: which side
 * reads wrong depends entirely on which clock the class is held to, which is
 * what report #200 (issue #768) is changing. That is why this rule reads the
 * lesson's own stored instant and never reasons about DST itself — it is right
 * on both sides of #768's switch-over without knowing which side it is on.
 *
 * ── Why a reference date was NOT the answer
 *
 * `viewerSlotLabel`'s header warns that threading a date through one leg "would
 * pass a test written with a Sydney or mainland-US viewer and still show the
 * Beijing family the wrong hour", because a Beijing viewer's whole hour of drift
 * lives in the COURSE leg. Rather than thread a date through both legs, this
 * module hands back the lesson's REAL INSTANTS, so the caller projects with
 * `projectInstantRange`, which takes no reference date and cannot drift. It is
 * the same route the surfaces fixed by reports #196/#197 already take, so a
 * repeating line cannot disagree with the dashboard or attendance list beside it.
 *
 * ── Both functions are PURE
 *
 * No hooks, no database, no `Date.now()` — `now` is always a parameter. They are
 * safe inside `.map()` and on either side of the network.
 */
import {
  composeInstantInTz,
  enumerateSessionDatesBetween,
} from '@/lib/timezone-utils';
import type { PROGRAM_TIMEZONES } from '@/lib/validators/program.schema';

/**
 * The clock a class is held to — `resolveClassClock`'s result
 * (`src/server/lib/class-clock.ts`). Typed as the program zones rather than a
 * bare string so a caller cannot pass `courses.timezone`, which report #200
 * established is the wrong lens and is in the middle of being replaced.
 */
export type SpeakingClock = (typeof PROGRAM_TIMEZONES)[number];

/**
 * The clock a weekly triple's wall-clock values are written in.
 *
 * `'UTC'` is a REAL frame here, not an escape hatch: the `availability_slots`
 * TABLE stores its triple already in UTC (`slot-label.ts`'s frame table), so an
 * occurrence composed for one of those lines is composed in UTC and only the
 * READER's leg is left to sample. Widened in part 4 rather than part 1 because
 * part 1 had no caller and could not yet see that both frames reach here.
 *
 * ⚠️ Still a closed union, for the reason `SpeakingClock` is one: a bare string
 * — above all `courses.timezone` — does not assign to it.
 */
export type OccurrenceClock = SpeakingClock | 'UTC';

/**
 * One laid-out lesson, as `section_instances` stores it. `startUtc`/`endUtc` are
 * the real moments, materialized per session and DST-correct by construction.
 *
 * `status` is `section_instances.override_status`. It matters: a CANCELLED
 * lesson is kept as a row for historical joins, and a repeating line that spoke
 * for one would advertise an hour for a lesson that is not happening.
 */
export type LaidOutLesson = {
  startUtc: Date;
  endUtc: Date;
  status?: 'scheduled' | 'cancelled' | 'extra' | 'modified';
};

/** The lesson a repeating line speaks for, and why that one. */
export type SpeakingLesson = {
  startUtc: Date;
  endUtc: Date;
  /**
   * `upcoming` — the next lesson at or after `now` (the normal case).
   * `finished` — the class has no lesson left, so its last one speaks. A
   * finished class's line is history, and history should read as it happened
   * rather than silently falling back to a summer sample.
   */
  basis: 'upcoming' | 'finished';
};

/**
 * The lesson a class's repeating line speaks for: its next laid-out lesson from
 * `now`, or its last one if the class has finished.
 *
 * ⚠️ A lesson IN PROGRESS counts as the next one — the test is `endUtc > now`,
 * not `startUtc > now`. A line that jumped to next week the instant a lesson
 * began would change the hour it advertises while a family was watching it, and
 * on a transition week it would change it to a different hour.
 *
 * Cancelled lessons are skipped. `extra` (an admin-added make-up) and `modified`
 * (a rescheduled meeting) are read-active everywhere else in the codebase and
 * are read-active here: both are real lessons at real moments, and a rescheduled
 * one's `start_utc` is its CURRENT moment, which is what a line should say.
 *
 * @param lessons any order; not mutated.
 * @returns `null` when there is no lesson to speak for — the caller keeps
 *   today's wording rather than inventing one. Part 4 must handle this: a class
 *   whose sessions have not been materialized yet is a real state, not a bug.
 */
export function pickSpeakingLesson(
  lessons: readonly LaidOutLesson[],
  now: Date,
): SpeakingLesson | null {
  const live = lessons
    .filter((l) => l.status !== 'cancelled')
    .slice()
    .sort((a, b) => a.startUtc.getTime() - b.startUtc.getTime());

  if (live.length === 0) return null;

  const nowMs = now.getTime();
  const upcoming = live.find((l) => l.endUtc.getTime() > nowMs);
  if (upcoming) {
    return { startUtc: upcoming.startUtc, endUtc: upcoming.endUtc, basis: 'upcoming' };
  }

  const last = live[live.length - 1];
  return { startUtc: last.startUtc, endUtc: last.endUtc, basis: 'finished' };
}

/** A repeating weekly pattern that has no class behind it yet. */
export type PatternOccurrenceInput = {
  /** Wall-clock weekday in `clock`, 0 = Sunday. */
  dayOfWeek: number;
  /** Wall-clock "HH:MM" in `clock`. */
  startTime: string;
  /** Wall-clock "HH:MM" in `clock`. An end at or before the start crosses midnight. */
  endTime: string;
  /**
   * The clock the pattern's wall-clock values are written in — the programme's
   * zone, via `resolveClassClock`, NOT `courses.timezone`; or `'UTC'` for a
   * triple read straight out of the `availability_slots` TABLE, which stores UTC.
   */
  clock: OccurrenceClock;
  /**
   * The run window, as calendar days (`YYYY-MM-DD`) ALREADY IN `clock`.
   *
   * ⚠️ `terms.start_date`/`end_date` are UTC INSTANTS, while
   * `classes.start_date` and `class_sections.start_date` are already course-tz
   * calendar days. Handing an instant's UTC date in here is a silent one-day
   * error west of UTC only — the hazard `enumerateSessionDatesBetween`'s header
   * documents. Project an instant to a calendar day in `clock` first.
   */
  windowStart: string;
  windowEnd: string;
  now: Date;
};

/**
 * The occurrence a repeating line speaks for when there is NO class to point at
 * — a pattern a parent said they are free for
 * (`catalog_requests.acceptable_patterns`), or a slot being considered while
 * classes are still being formed.
 *
 * The next time that weekday comes round inside the run window, composed in the
 * programme's clock; or the last occurrence in the window once it has passed.
 * Same rule as `pickSpeakingLesson`, applied where there is nothing to look up.
 *
 * @returns `null` when the weekday never falls inside the window.
 */
export function pickSpeakingOccurrence(
  input: PatternOccurrenceInput,
): SpeakingLesson | null {
  const { dayOfWeek, startTime, endTime, clock, windowStart, windowEnd, now } = input;

  // ⚠️ The slot's LENGTH, never a comparison of its two times.
  //
  // `weekly-slot-overlap.audit.test.ts` bans comparing a start against an end
  // with an ordering operator anywhere under `src/lib` or `src/server`, because
  // six of seven historical copies of that shape were fail-open for a slot
  // crossing midnight. The modulo below is the same arithmetic `weeklyInterval`
  // (`src/server/services/weekly-slot.helper.ts`) uses to turn a wrapped end
  // into a real duration — 23:30 → 00:25 is 55 minutes, not minus 1385. It is
  // reimplemented rather than imported because that module pulls in `drizzle-orm`
  // at runtime, and part 4's callers are client components.
  const startMinutes = minutesOfDay(startTime);
  const lengthMinutes =
    (minutesOfDay(endTime) - startMinutes + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  const dates = enumerateSessionDatesBetween(windowStart, windowEnd, dayOfWeek, clock);
  const occurrences: LaidOutLesson[] = dates.map((date) => {
    const startUtc = composeInstantInTz(date, startTime, clock);
    return {
      startUtc,
      // Real minutes from the real start, rather than composing the end
      // separately. Composing both ends independently would have to reason about
      // which calendar day the end falls on, and on a transition night one of
      // the two wall-clocks it samples may not exist at all. A lesson's length
      // is the thing that is actually fixed.
      endUtc: new Date(startUtc.getTime() + lengthMinutes * 60_000),
    };
  });

  return pickSpeakingLesson(occurrences, now);
}

const MINUTES_PER_DAY = 1440;

/** `"HH:MM"` as minutes since midnight. */
function minutesOfDay(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  const minutes = Number(h) * 60 + Number(m);
  if (!Number.isFinite(minutes)) {
    // Fail loud, for the reason `weeklyInterval` does: NaN makes every
    // comparison false, so a malformed slot would quietly answer every question
    // with "no".
    throw new Error(`minutesOfDay: malformed time ${JSON.stringify(hhmm)}`);
  }
  return minutes;
}
